import { fetchWithHandling } from '../utils/common-utils';
import {
  Task,
  PagedTasks,
  TaskStatistics,
  TaskStatus,
  DeleteMode,
  CreateTaskRequest,
  UpdateTaskRequest,
  UpdateTaskStatusRequest,
} from '../utils/interfaces';

const BASE_URL = `${process.env.REACT_APP_BACKEND_API_PREFIX}/api/v1/tasks`;

export const getAllPaginatedTasks = async (
  token: string,
  onSessionExpired: () => void,
  filters: {
    pageNumber?: number;
    pageSize?: number;
    employeeId?: string;
    customerId?: number;
    status?: TaskStatus;
    dateFrom?: string;
    dateTo?: string;
    createdFrom?: string;
    createdTo?: string;
    reportNumber?: string;
    reportStatus?: string;
  } = {}
): Promise<PagedTasks | undefined> => {
  try {
    const params = new URLSearchParams();
    if (filters.pageNumber !== undefined) params.set('pageNumber', String(filters.pageNumber));
    if (filters.pageSize !== undefined) params.set('pageSize', String(filters.pageSize));
    if (filters.employeeId) params.set('employeeId', filters.employeeId);
    if (filters.customerId) params.set('customerId', String(filters.customerId));
    if (filters.status) params.set('status', filters.status);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.createdFrom) params.set('createdFrom', filters.createdFrom);
    if (filters.createdTo) params.set('createdTo', filters.createdTo);
    if (filters.reportNumber) params.set('reportNumber', filters.reportNumber);
    if (filters.reportStatus) params.set('reportStatus', filters.reportStatus);

    const url = `${BASE_URL}/paginated${params.size > 0 ? `?${params.toString()}` : ''}`;

    const jsonData = await fetchWithHandling(
      url,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );

    return jsonData as PagedTasks | null;
  } catch (error) {
    return undefined;
  }
};

export const getTaskById = async (
  token: string,
  onSessionExpired: () => void,
  id: number
): Promise<Task | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/${id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );

    return jsonData as Task | null;
  } catch (error) {
    return undefined;
  }
};

export const createTask = async (
  token: string,
  onSessionExpired: () => void,
  data: CreateTaskRequest
): Promise<Task | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      BASE_URL,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      },
      onSessionExpired
    );

    return jsonData as Task | null;
  } catch (error) {
    return undefined;
  }
};

export const updateTask = async (
  token: string,
  onSessionExpired: () => void,
  id: number,
  data: UpdateTaskRequest
): Promise<Task | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/${id}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      },
      onSessionExpired
    );

    return jsonData as Task | null;
  } catch (error) {
    return undefined;
  }
};

export const deleteTask = async (
  token: string,
  onSessionExpired: () => void,
  id: number,
  deleteMode: DeleteMode = 'ONLY_THIS'
): Promise<boolean> => {
  try {
    const res = await fetchWithHandling(
      `${BASE_URL}/${id}?deleteMode=${deleteMode}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );
    return res !== null && res !== undefined;
  } catch (error) {
    return false;
  }
};

export const deleteBulkTasks = async (
  token: string,
  onSessionExpired: () => void,
  ids: number[],
  deleteMode: DeleteMode = 'ONLY_THIS'
): Promise<boolean> => {
  try {
    const res = await fetchWithHandling(
      `${BASE_URL}/bulk`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids, deleteMode }),
      },
      onSessionExpired
    );
    return res !== null && res !== undefined;
  } catch (error) {
    return false;
  }
};

export const updateTaskStatus = async (
  token: string,
  onSessionExpired: () => void,
  id: number,
  payload: UpdateTaskStatusRequest
): Promise<Task | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/${id}/status`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      },
      onSessionExpired
    );

    return jsonData as Task | null;
  } catch (error) {
    return undefined;
  }
};


export const getTaskStatistics = async (
  token: string,
  onSessionExpired: () => void,
  filters: {
    dateFrom?: string;
    dateTo?: string;
    employeeId?: string;
  } = {}
): Promise<TaskStatistics | undefined> => {
  try {
    const params = new URLSearchParams();
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.employeeId) params.set('employeeId', filters.employeeId);

    const url = `${BASE_URL}/statistics${params.size > 0 ? `?${params.toString()}` : ''}`;

    const jsonData = await fetchWithHandling(
      url,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );

    return jsonData as TaskStatistics | null;
  } catch (error) {
    return undefined;
  }
};

export const getMyTasks = async (
  token: string,
  onSessionExpired: () => void,
  filters: {
    pageNumber?: number;
    pageSize?: number;
    status?: TaskStatus;
    dateFrom?: string;
    dateTo?: string;
  } = {}
): Promise<PagedTasks | undefined> => {
  try {
    const params = new URLSearchParams();
    if (filters.pageNumber !== undefined) params.set('pageNumber', String(filters.pageNumber));
    if (filters.pageSize !== undefined) params.set('pageSize', String(filters.pageSize));
    if (filters.status) params.set('status', filters.status);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);

    const url = `${BASE_URL}/my-tasks${params.size > 0 ? `?${params.toString()}` : ''}`;

    const jsonData = await fetchWithHandling(
      url,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );

    return jsonData as PagedTasks | null;
  } catch (error) {
    return undefined;
  }
};
